import React, { FC } from 'react';
import {
    FormControl,
    FormControlLabel,
    FormControlLabelProps,
    Switch as SwitchBase,
    SwitchProps,
    switchClasses,
    formControlLabelClasses,
    buttonBaseClasses,
} from '@mui/material';

import { fileUrl } from 'Modules/ui';

import PadlockIcon from '../../icons/padlock.svg';


export type { SwitchProps };

export const Switch: FC<SwitchProps> = ({ sx, name, ...props }) => (
    <SwitchBase
        data-testid={`switch-${name}`}
        focusVisibleClassName={`.${buttonBaseClasses.focusVisible}`}
        disableRipple
        sx={[({ spacing, palette, transitions }) => ({
            width: spacing(13),
            height: spacing(8),
            padding: 0,
            [`& .${switchClasses.switchBase}`]: {
                padding: 0,
                margin: 0.5,
                transitionDuration: '300ms',
                [`&.${switchClasses.checked}`]: {
                    transform: `translateX(${spacing(5)})`,
                    color: palette.grayscale.shade0,
                    [`& + .${switchClasses.track}`]: {
                        backgroundColor: palette.general.primary50,
                        opacity: 1,
                        border: 0,
                    },
                },
                [`&.${switchClasses.disabled}`]: {
                    [`& + .${switchClasses.track}`]: {
                        opacity: 1,
                    },
                    [`&.${switchClasses.disabled} + .${switchClasses.track}`]: {
                        backgroundColor: palette.general.primary50,
                    },
                    [`& .${switchClasses.thumb}`]: {
                        backgroundColor: palette.grayscale.shade0,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: spacing(4),
                        backgroundImage: `url("${fileUrl(PadlockIcon)}")`,
                    },
                },
                [`&.${buttonBaseClasses.focusVisible} .${switchClasses.thumb}`]: {
                    color: palette.general.primary50,
                    border: 6,
                    borderColor: palette.grayscale.shade0,
                },
            },
            [`& .${switchClasses.thumb}`]: {
                boxSizing: 'border-box',
                width: spacing(7),
                height: spacing(7),
            },
            [`& .${switchClasses.track}`]: {
                borderRadius: spacing(4),
                backgroundColor: palette.grayscale.shade20,
                opacity: 1,
                transition: transitions.create(['background-color'], {
                    duration: 500,
                }),
            },
        }),
        ...Array.isArray(sx) ? sx : [sx]]}
        {...props}
    />
);

export type SwitchWithLabelProps = Omit<FormControlLabelProps, 'control'> & {
    switchProps?: SwitchProps;
};

export const SwitchWithLabel: FC<SwitchWithLabelProps> = ({
    switchProps, sx, componentsProps, ...props
}) => (
    <FormControl>
        <FormControlLabel
            componentsProps={{
                ...componentsProps,
                typography: {
                    variant: 'regularBody2',
                    color: 'grayscale.shade95',
                    ml: 4,
                    ...componentsProps?.typography,
                },
            }}
            control={<Switch {...switchProps} />}
            sx={[({ palette }) => ({
                mx: 0,
                [`& .${formControlLabelClasses.label}.${formControlLabelClasses.disabled}`]: {
                    color: palette.grayscale.shade95,
                },
            }),
            ...Array.isArray(sx) ? sx : [sx]]}
            {...props}
        />
    </FormControl>
);
